import { read, utils } from "xlsx";
import { FieldConfig } from "./configVariable";
import { ImportMethod, DocumentType, MappingSetKey } from "./models";

/**
 * Sort field keys based on their sortOrder property in field configs
 * @param a First field key
 * @param b Second field key
 * @param fieldConfigs Map of field configs
 * @returns Sort order (-1, 0, or 1)
 */
export const sortFieldsByConfig = (
  a: string,
  b: string,
  fieldConfigs?: { [key: string]: FieldConfig }
): number => {
  // Use the sortOrder property if available
  const fieldConfigA = fieldConfigs?.[a];
  const fieldConfigB = fieldConfigs?.[b];

  // If both have sortOrder, sort by that
  if (
    fieldConfigA?.sortOrder !== undefined &&
    fieldConfigB?.sortOrder !== undefined
  ) {
    return fieldConfigA.sortOrder - fieldConfigB.sortOrder;
  }

  // If only one has sortOrder, prioritize the one with sortOrder
  if (fieldConfigA?.sortOrder !== undefined) return -1;
  if (fieldConfigB?.sortOrder !== undefined) return 1;

  // Default: sort by field key alphabetically
  return a.localeCompare(b);
};

export const getFileExtension = (file: File) => {
  return file.name.split(".").pop()?.toLowerCase();
};

export const readFileHeaders = ({
  file,
  onFileRead,
}: {
  file: File;
  onFileRead: (headers: string[]) => void;
}) => {
  const fileExtension = getFileExtension(file);
  if (fileExtension === "csv") {
    // Handle CSV
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const headers = text.split("\n")[0].split(",");
      onFileRead(headers.map((h) => h.replace("\r", "").replace("\n", "")));
    };
    reader.readAsText(file);
  } else if (["xlsx", "xls", "xlsm"].includes(fileExtension || "")) {
    // Handle Excel
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = e.target?.result;
      try {
        const workbook = read(data, { type: "array" });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];

        // Get headers from first row
        const headers = utils.sheet_to_json(worksheet, {
          header: 1,
        })[0] as string[];

        onFileRead(headers.map((h) => h.replace("\r", "").replace("\n", "")));
      } catch (error) {
        console.error("Error reading Excel file:", error);
        // Handle error - maybe show a toast or modal
      }
    };
    reader.readAsArrayBuffer(file);
  }
};

export const getMappingSetKey = (
  importMethod: ImportMethod,
  documentType: DocumentType
): MappingSetKey | undefined => {
  if (
    importMethod === ImportMethod.HEADERS_ONLY &&
    documentType === DocumentType.ORDER
  ) {
    return "importOrders";
  } else if (
    importMethod === ImportMethod.HEADERS_ONLY &&
    documentType === DocumentType.INVOICE
  ) {
    return "importInvoices";
  } else if (
    importMethod === ImportMethod.HEADERS_AND_DETAILS &&
    documentType === DocumentType.ORDER
  ) {
    return "importOrdersSales";
  } else if (
    importMethod === ImportMethod.HEADERS_AND_DETAILS &&
    documentType === DocumentType.INVOICE
  ) {
    return "importInvoicesSales";
  }
  return undefined;
};

export const normalizeColumnName = (columnName: string) =>
  columnName
    .trim()
    .replaceAll(/[ \-_*]/g, "")
    .toLowerCase();
